import { format, isToday, isBefore, endOfDay, startOfDay, differenceInCalendarDays } from 'date-fns';

import {
  CallNumber,
  CallPerson,
  CallOutcome,
  CallQueueView,
  CallListEntry,
  CallNumberType,
  CallAttemptGapUnit,
  CallListEntryStatus,
} from 'src/services/call-lists/call-lists.types';

/** How many more queue rows each "Show more" loads. */
export const PAGE_STEP = 25;

export type OutcomeColor = 'success' | 'warning' | 'error' | 'default';

export const OUTCOMES: CallOutcome[] = [
  CallOutcome.CONNECTED,
  CallOutcome.VOICEMAIL,
  CallOutcome.NO_ANSWER,
  CallOutcome.WRONG_NUMBER,
  CallOutcome.DO_NOT_CALL,
];

export const OUTCOME_COLOR: Record<CallOutcome, OutcomeColor> = {
  [CallOutcome.CONNECTED]: 'success',
  [CallOutcome.VOICEMAIL]: 'warning',
  [CallOutcome.NO_ANSWER]: 'default',
  [CallOutcome.WRONG_NUMBER]: 'error',
  [CallOutcome.DO_NOT_CALL]: 'error',
};

export const OUTCOME_ICON: Record<CallOutcome, string> = {
  [CallOutcome.CONNECTED]: 'solar:phone-calling-bold',
  [CallOutcome.VOICEMAIL]: 'solar:voicemail-bold',
  [CallOutcome.NO_ANSWER]: 'solar:end-call-bold',
  [CallOutcome.WRONG_NUMBER]: 'solar:phone-rounded-bold',
  [CallOutcome.DO_NOT_CALL]: 'solar:forbidden-circle-bold',
};

export const personName = (person: Pick<CallPerson, 'first_name' | 'last_name'>) =>
  `${person.first_name ?? ''} ${person.last_name ?? ''}`.trim();

export const initials = (person: Pick<CallPerson, 'first_name' | 'last_name'>) =>
  `${person.first_name?.[0] ?? ''}${person.last_name?.[0] ?? ''}`.toUpperCase() || '?';

export const personSubtitle = (person: CallPerson) => [person.title, person.company].filter(Boolean).join(' · ');

/** "9:00 am" today, "Overdue · Mon 12 Oct" before today, "Thu 15 Oct" after it. */
export function dueLabel(value: string | null, overdueLabel: string): string {
  if (!value) return '';
  const date = new Date(value);
  const today = startOfDay(new Date());
  if (isBefore(date, today)) {
    return `${overdueLabel} · ${format(date, 'EEE d MMM')}`;
  }
  if (differenceInCalendarDays(date, today) === 0) {
    return format(date, 'h:mm a').toLowerCase();
  }
  return format(date, 'EEE d MMM');
}

export const formatDateTime = (value: string | Date | null | undefined) =>
  value ? format(new Date(value), 'EEE d MMM, h:mm a').replace('AM', 'am').replace('PM', 'pm') : '';

export const formatDate = (value: string | Date | null | undefined) => (value ? format(new Date(value), 'EEE d MMM') : '');

export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return minutes > 0 ? `${minutes} min ${rest} sec` : `${rest} sec`;
}

/** The queue view a person sits in today: due (overdue included), upcoming, or called today. */
export function queueViewFor(entry: Pick<CallListEntry, 'status' | 'next_call_at' | 'last_attempt_at'>): CallQueueView {
  if (entry.status === CallListEntryStatus.ACTIVE && entry.next_call_at) {
    return new Date(entry.next_call_at) <= endOfDay(new Date()) ? CallQueueView.DUE : CallQueueView.UPCOMING;
  }
  if (entry.last_attempt_at && isToday(new Date(entry.last_attempt_at))) {
    return CallQueueView.DONE;
  }
  return CallQueueView.DUE;
}

/** A stable key for one dialable number, so a picker can tell two custom fields apart. */
export const numberKey = (number: Pick<CallNumber, 'type' | 'custom_field_config_id'>) =>
  number.type === CallNumberType.CUSTOM_FIELD ? `${number.type}:${number.custom_field_config_id}` : number.type;

export const gapUnitKey = (unit: CallAttemptGapUnit) =>
  unit === CallAttemptGapUnit.BUSINESS_DAYS ? 'business_days' : 'calendar_days';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const formatHour = (hour: number) => {
  const suffix = hour >= 12 && hour < 24 ? 'pm' : 'am';
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}${suffix}`;
};

/**
 * The agency's working week in a line, e.g. "Mon–Fri, 9am–5pm". Business hours are whole hours. An
 * agency that never set them works Mon–Fri 9–5, which is what the scheduler uses too.
 */
export function describeWorkingHours(weekdays?: { day: number; start: number; end: number }[]): string {
  const days = weekdays?.length ? [...weekdays].sort((a, b) => a.day - b.day) : [1, 2, 3, 4, 5].map((day) => ({ day, start: 9, end: 17 }));
  const sameHours = days.every((day) => day.start === days[0].start && day.end === days[0].end);
  const consecutive = days.every((day, index) => index === 0 || day.day === days[index - 1].day + 1);

  if (sameHours) {
    const range = consecutive && days.length > 1
      ? `${DAY_NAMES[days[0].day]}–${DAY_NAMES[days[days.length - 1].day]}`
      : days.map((day) => DAY_NAMES[day.day]).join(', ');
    return `${range}, ${formatHour(days[0].start)}–${formatHour(days[0].end)}`;
  }

  return days.map((day) => `${DAY_NAMES[day.day]} ${formatHour(day.start)}–${formatHour(day.end)}`).join(', ');
}
